import React, { FC, Fragment, ChangeEvent, useState, useRef, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { UserContext } from "features/account";
import { renderWatermarkCanvas, renderWatermarkPDF } from 'utils/filigrane_utils';
import { FiligraneStep, UploadedFile, ProcessedResult } from './filigrane.types';
import StepImport from './StepImport';
import StepPreview from './StepPreview';
import StepText from './StepText';
import StepResult from './StepResult';
import WatermarkProcessing from './WatermarkProcessing';
import FAQModal from './FAQModal';
import VideoModal from './VideoModal';
import './FiligraneImporterPage.scss';

// Assets
import watermarkImgSrc from 'assets/img/watermark_2.png';
import watermarkPDFSrc from 'assets/img/watermark_2.png';
import imgVideoPreview from 'assets/img/imgPrevFiligraneVideo.png';
import { ReactComponent as IconFileCheck } from "assets/img/icons/icon_FileCheck.svg";

import { ReactComponent as IconFiles } from 'assets/img/icons/icon_Files.svg';
import { ReactComponent as IconFileProtected } from 'assets/img/icons/icon_FileProtected.svg';
import { ReactComponent as IconFileError } from 'assets/img/icons/icon_FileError.svg';
import { ReactComponent as IconShiled } from 'assets/img/icons/icon_Shiled2.svg';
import { ReactComponent as IconFile } from 'assets/img/icons/icon_File2.svg';
import { ReactComponent as IconFileUpload } from 'assets/img/icons/icon_UploadFile.svg';
import { ReactComponent as IconTexte } from 'assets/img/icons/icon_Texte.svg';
import iconShield from 'assets/img/icons/icon_shield-Save.svg';
import { ReactComponent as IconPlay } from 'assets/img/icons/icon_Play.svg';
import PageHeader from "components/PageHeader";

const STEPPER_ITEMS = [
  { icon: IconFileUpload, label: 'Import' },
  { icon: IconTexte, label: 'Texte' },
  { icon: IconFile, label: 'Résultat' },
];

const WHY_ITEMS = [
  { icon: IconFileProtected, label: "Protéger mes documents avant tout envoi" },
  { icon: IconFileError, label: 'Rendre le document inutilisable par une tierce personne' },
  { icon: IconShiled, label: 'Favorise la protection de mon identité et de mes données' },
];

const FAQ_ITEMS = [
  'Comment fonctionne mon filigrane ?',
  'Pour quel usage dois-je utiliser le filigrane ?',
  'Quels types de documents peuvent être filigranés ?',
];

const FiligraneImporterPage: FC = () => {
  const { userType } = useContext(UserContext);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const prefix = userType === 'partner' ? 'professionnels' : 'particuliers';

  const [step, setStep] = useState<FiligraneStep>('import');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [resultSelectedIds, setResultSelectedIds] = useState<string[]>([]);
  const [watermarkText, setWatermarkText] = useState('Document protégé par ID Protect');
  const [processedResults, setProcessedResults] = useState<ProcessedResult[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [faqOpenIndex, setFaqOpenIndex] = useState<number | null>(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [failedFiles, setFailedFiles] = useState<{ name: string; reason: string }[]>([]);

  // Stepper index: import/preview → 0, text → 1, result → 2
  const stepperIndex = step === 'result' ? 2 : step === 'text' ? 1 : 0;

  // File count shown in the banner
  const bannerCount = step === 'result' ? processedResults.length : uploadedFiles.length;

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newUploads: UploadedFile[] = files.map(f => ({
      id: `${f.name}-${f.size}-${Date.now()}-${Math.random()}`,
      file: f,
      // Object URL for images AND PDFs — the card renders PDFs in a scaled iframe
      previewUrl: f.type.startsWith('image/') || f.type === 'application/pdf'
        ? URL.createObjectURL(f)
        : '',
    }));
    if (step === 'preview') {
      setUploadedFiles(prev => {
        const merged = [...prev, ...newUploads];
        setSelectedIds(merged.map(u => u.id));
        return merged;
      });
    } else {
      setUploadedFiles(newUploads);
      setSelectedIds(newUploads.map(u => u.id));
      setStep('preview');
    }
    e.target.value = '';
  };

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => prev.length === uploadedFiles.length ? [] : uploadedFiles.map(f => f.id));
  }, [uploadedFiles]);

  const toggleResultSelect = useCallback((id: string) => {
    setResultSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  }, []);

  const toggleResultSelectAll = useCallback(() => {
    setResultSelectedIds(prev =>
      prev.length === processedResults.length ? [] : processedResults.map(r => r.id)
    );
  }, [processedResults]);

  const deleteSelected = useCallback(() => {
    setUploadedFiles(prev => {
      const next = prev.filter(f => !selectedIds.includes(f.id));
      setSelectedIds(next.map(f => f.id));
      return next;
    });
  }, [selectedIds]);

  const applyWatermark = useCallback(async () => {
    setIsProcessing(true);
    setProcessedCount(0);
    const toProcess = uploadedFiles.filter(f => selectedIds.includes(f.id));
    const results: ProcessedResult[] = [];
    const failures: { name: string; reason: string }[] = [];

    for (const uf of toProcess) {
      try {
        if (uf.file.type === 'application/pdf') {
          const buffer = await uf.file.arrayBuffer();
          const bytes = await renderWatermarkPDF(buffer, watermarkPDFSrc, watermarkText);
          if (bytes) {
            const blob = new Blob([bytes], { type: 'application/pdf' });
            const previewUrl = URL.createObjectURL(blob);
            results.push({ id: uf.id, name: uf.file.name, previewUrl, blob, type: 'pdf' });
          } else {
            failures.push({ name: uf.file.name, reason: 'Le filigrane n\'a pas pu être généré' });
          }
        } else {
          if (!canvasRef.current) continue;
          await renderWatermarkCanvas(watermarkText, canvasRef.current, uf.file, watermarkImgSrc);
          const blob = await new Promise<Blob>((resolve, reject) =>
            canvasRef.current!.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')))
          );
          const previewUrl = URL.createObjectURL(blob);
          results.push({ id: uf.id, name: uf.file.name, previewUrl, blob, type: 'image' });
        }
        setProcessedCount(results.length);
      } catch (err) {
        console.error(`Error processing ${uf.file.name}:`, err);
        failures.push({
          name: uf.file.name,
          reason: err instanceof Error ? err.message : 'Erreur inconnue',
        });
      }
    }

    setProcessedResults(results);
    setResultSelectedIds(results.map(r => r.id));
    setFailedFiles(failures);
    setIsProcessing(false);
    setStep('result');
  }, [uploadedFiles, selectedIds, watermarkText]);

  const handleDownloadSelected = useCallback(() => {
    processedResults
      .filter(r => resultSelectedIds.includes(r.id))
      .forEach(r => saveAs(r.blob, `filigrane_${r.name}`));
  }, [processedResults, resultSelectedIds]);

  const handleDeleteResult = useCallback((id: string) => {
    setProcessedResults(prev => prev.filter(r => r.id !== id));
    setResultSelectedIds(prev => prev.filter(i => i !== id));
  }, []);

  const handleReset = () => {
    setStep('import');
    setUploadedFiles([]);
    setSelectedIds([]);
    setProcessedResults([]);
    setResultSelectedIds([]);
    setFailedFiles([]);
    setWatermarkText('Document protégé par ID Protect');
  };

  return (
    <div className="filigranePage">
      <PageHeader title={"filigrane"} description={"Ajouter un filigrane"} notification={false} protectAlert={false}>
      </PageHeader>

      <div className="filigrane-page">
        {/* Hidden canvas for watermark rendering */}
        <canvas ref={canvasRef} style={{ display: 'none' }} aria-hidden="true" />
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          aria-hidden="true"
        />


        {/* ── Main body ─────────────────────────────────────────────────── */}
        <div className="filigrane-page__body">

          {/* Left: stepper + card */}
          <div className="filigrane-page__left">

            {/* Stepper */}
            <div className="filigrane-page__stepper" aria-label="Étapes" role="list">
              {STEPPER_ITEMS.map((s, i) => (
                <Fragment key={i}>
                  <div
                    className={`filigrane-page__step-box${i > stepperIndex ? ' filigrane-page__step-box--inactive' : ''}`}
                    role="listitem"
                    aria-label={`Étape ${i + 1}: ${s.label}`}
                    aria-current={i === stepperIndex ? 'step' : undefined}
                  >
                    <s.icon fill={"#000"}/>
                    {i === stepperIndex && <div className="filigrane-page__step-dot" aria-hidden="true" />}
                  </div>
                  {i < STEPPER_ITEMS.length - 1 && (
                    <div className="filigrane-page__step-line" aria-hidden="true" />
                  )}
                </Fragment>
              ))}
            </div>

            {/* Main card */}
            <div className="filigrane-page__card">
              {/* Card header (persistent) */}
              <div className="filigrane-page__card-header">
                <h2>Aposer un Filigrane</h2>
                <div className="filigrane-page__card-meta">
                  <span className="filigrane-page__card-meta-text">Formats acceptés : PDF, PNG, JPG, JPEG</span>
                  <div className="filigrane-page__card-meta-dot" aria-hidden="true" />
                  <span className="filigrane-page__card-meta-text">Taille maximale : 10 Mo</span>
                </div>
              </div>

              {/* Banner (persistent) */}
              <div className="filigrane-page__card-banner">
                {step === 'import' ? (
                  <>
                    <IconFiles fill={"#272f91"} width={17}/>
                    <span className="filigrane-page__banner-text">ID Protect ne conserve aucune copie</span>
                  </>
                ) : (
                  <>
                    <div className="filigrane-page__banner-badge">
                      <IconFileCheck width={14} className="filigrane-page__banner-shield"/>
                      <span className="filigrane-page__banner-count">{bannerCount}</span>
                    </div>
                    <span className="filigrane-page__banner-text">ID Protect ne conserve aucune copie</span>
                  </>
                )}
              </div>

              {/* Step content */}
              {step === 'import' && (
                <StepImport onUploadClick={() => fileInputRef.current?.click()} />
              )}
              {step === 'preview' && (
                <StepPreview
                  files={uploadedFiles}
                  selectedIds={selectedIds}
                  onToggle={toggleSelect}
                  onSelectAll={toggleSelectAll}
                  onDeleteSelected={deleteSelected}
                  onAddMore={() => fileInputRef.current?.click()}
                  onNext={() => setStep('text')}
                />
              )}
              {step === 'text' && !isProcessing && (
                <StepText
                  watermarkText={watermarkText}
                  onChange={setWatermarkText}
                  onApply={applyWatermark}
                  onBack={() => setStep('preview')}
                  isProcessing={false}
                />
              )}
              {step === 'text' && isProcessing && (
                <WatermarkProcessing
                  current={processedCount}
                  total={uploadedFiles.filter(f => selectedIds.includes(f.id)).length}
                />
              )}
              {step === 'result' && (
                <StepResult
                  results={processedResults}
                  selectedIds={resultSelectedIds}
                  failedFiles={failedFiles}
                  onToggle={toggleResultSelect}
                  onSelectAll={toggleResultSelectAll}
                  onDelete={handleDeleteResult}
                  onDownloadSelected={handleDownloadSelected}
                  onReset={handleReset}
                />
              )}
            </div>
          </div>

          {/* Right info column */}
          <aside className="filigrane-page__right" aria-label="Informations">

            {/* Pourquoi */}
            <div className="filigrane-page__info-card">
              <h3>Pourquoi aposer un Filigrane ?</h3>
              <div className="filigrane-page__why-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {WHY_ITEMS.map((item, i) => (
                  <div key={i} className="filigrane-page__why-item">
                    <div className="filigrane-page__why-icon-box">
                      <item.icon />
                    </div>
                    <span className="filigrane-page__why-text">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div className="filigrane-page__info-card">
              <h3>FAQ</h3>
              <div className="filigrane-page__faq-list">
                {FAQ_ITEMS.map((q, i) => (
                  <Fragment key={i}>
                    <button
                      className="filigrane-page__faq-item"
                      type="button"
                      onClick={() => setFaqOpenIndex(i)}
                    >
                      {q}
                    </button>
                    {i < FAQ_ITEMS.length - 1 && <hr className="filigrane-page__faq-divider" />}
                  </Fragment>
                ))}
              </div>
            </div>

            {/* Présentation */}
            <div className="filigrane-page__presentation-card">
              <h3
                className={"pointer"}
                onClick={() => setVideoModalOpen(true)} >Présentation de l'outil</h3>
              <button
                className="filigrane-page__video-link"
                onClick={() => setVideoModalOpen(true)}
                aria-label="Voir la présentation en vidéo"
                type="button"
              >
                <img src={imgVideoPreview} alt="" className="filigrane-page__video-thumb" />
                <div className="filigrane-page__video-overlay" aria-hidden="true" />
                <IconPlay fill={"#fff"} className="filigrane-page__play-icon" />
              </button>
            </div>

          </aside>
        </div>
      </div>

      <FAQModal
        isOpen={faqOpenIndex !== null}
        onClose={() => setFaqOpenIndex(null)}
        questionIndex={faqOpenIndex ?? 0}
      />
      <VideoModal
        isOpen={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
      />
    </div>

  );
};

export default FiligraneImporterPage;
